import { Link } from "react-router-dom";
import { MapPin, Calendar, Ticket } from "lucide-react";
import { motion } from "framer-motion";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const CATS = { "trail-rides": "Trail Rides", concerts: "Concerts", corporate: "Corporate", sports: "Sports & Arena" };

export const EventCard = ({ event, index = 0, featured = false }) => {
  const tiers = (event.tiers || []).filter((t) => t.active !== false);
  const minPrice = tiers.length ? Math.min(...tiers.map((t) => t.price)) : null;
  const isPast = new Date(event.date).getTime() < Date.now();

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: Math.min(index * 0.08, 0.4) }}
      className={`group overflow-hidden rounded-2xl border border-neutral-800 bg-[#0a0a0a] transition-all duration-200 hover:-translate-y-1 hover:border-cyan-500/50 ${
        featured ? "md:col-span-8" : "md:col-span-4"
      }`}
      data-testid={`event-card-${event.slug}`}
    >
      <Link to={`/events/${event.slug}`} className="block" data-testid={`event-card-detail-link-${event.slug}`}>
        <div className={`relative overflow-hidden bg-stone-900 ${featured ? "h-64 md:h-80" : "h-52"}`}>
          {event.hero_image ? (
            <img
              src={event.hero_image}
              alt={event.title}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-stone-800 to-black font-display text-4xl font-black uppercase text-cyan-500/40">
              EON
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#000000] via-[#000000]/30 to-transparent" />
          <div className="absolute left-4 top-4 flex flex-col items-center rounded-lg border border-cyan-500/40 bg-black/70 px-3 py-1.5 backdrop-blur">
            <Calendar className="h-3.5 w-3.5 text-cyan-400" />
            <span className="mt-0.5 font-mono-label text-[10px] uppercase tracking-wider text-cyan-200">{fmtDate(event.date)}</span>
          </div>
          <span className="absolute bottom-4 left-4 rounded-full border border-white/15 bg-black/60 px-3 py-1 font-mono-label text-[10px] uppercase tracking-wider text-chrome backdrop-blur" data-testid={`event-category-badge-${event.slug}`}>
            {CATS[event.category] || "Live Event"}
          </span>
          {isPast && (
            <span className="absolute right-4 top-4 rounded-full border border-stone-600 bg-black/70 px-3 py-1 font-mono-label text-[10px] uppercase text-neutral-300">
              Past
            </span>
          )}
          {event.featured && !isPast && (
            <span className="absolute right-4 top-4 rounded-full border border-cyan-500/40 bg-cyan-950/70 px-3 py-1 font-mono-label text-[10px] uppercase tracking-wider text-cyan-300">
              Next Up
            </span>
          )}
        </div>
      </Link>

      <div className="p-6">
        <Link to={`/events/${event.slug}`}>
          <h3 className={`font-display font-black uppercase leading-tight text-stone-100 group-hover:text-cyan-50 ${featured ? "text-2xl md:text-3xl" : "text-xl"}`}>
            {event.title}
          </h3>
        </Link>
        <div className="mt-2 flex items-center gap-1.5 text-sm text-neutral-400">
          <MapPin className="h-4 w-4 text-cyan-500" />
          {event.location_name}, {event.city} {event.state}
        </div>
        {featured && <p className="mt-3 line-clamp-2 text-sm text-neutral-400">{event.subtitle}</p>}

        <div className="mt-5 flex items-center justify-between">
          <div>
            {minPrice != null ? (
              <>
                <span className="font-mono-label text-[10px] uppercase text-neutral-500">From</span>
                <div className="font-display text-2xl font-black text-cyan-400">${minPrice}</div>
              </>
            ) : (
              <span className="font-mono-label text-[11px] uppercase text-neutral-500">
                {isPast ? "Recap" : "Coming soon"}
              </span>
            )}
          </div>
          <Link
            to={`/events/${event.slug}`}
            data-testid={`event-get-tickets-${event.slug}`}
            className="flex items-center gap-2 rounded-full btn-gold px-5 py-2.5 font-display font-bold uppercase tracking-wide"
          >
            <Ticket className="h-4 w-4" />
            {isPast ? "View" : "Get Tickets"}
          </Link>
        </div>
      </div>
    </motion.div>
  );
};
